import {
  useWSModal,
  WSActions,
  WSAlert,
  WSCard,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import {
  queriesExternalBankAccounts,
  queriesPaymentCards,
  queriesPayers,
  queriesPayouts
} from "@query";
import { externalBankAccountsApi, paymentCardsApi, payoutsApi } from "@api";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { strongETag } from "@utils/strongETag";
import { ErrorAlert } from "@components/ErrorAlert";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_PAYMENT_CARDS } from "../../../../query/paymentCards/keys";
import {
  QUERY_USERS_ACCOUNTS,
  QUERY_USERS_CLIENT
} from "../../../../query/users/keys";
import { usersService } from "../../../../services/users";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { useMethodLabelProps } from "../LabelMethod/useMethodLabelProps";
import { MethodType } from "./types";

type Props = {
  onClose: (isRemoved: boolean) => void;
  id: string;
  type: MethodType;
};

/**
 * TODO(v3): this modal does not know whether the method it is removing is the
 * account's *default payment method*, so it can neither warn about that usage
 * nor clear the default, nor run the "a payer with Active engagements must keep
 * a method on file" replacement guard.
 *
 * That default only ever lived on the v1 client profile
 * (`GET /users/user/client/:id` → `profile.defaultPaymentMethod`), which is
 * retired; v3 carries one per payer relationship and a read-only one per
 * contractor, but none on the account. Reading it here pinned Remove disabled
 * forever, so the read is gone. Restore all four behaviours together once v3
 * exposes an account-level default payment method.
 */
export const ModalRemove: React.FC<Props> = props => {
  const userId = useUserId();
  const queryPayerSettings = queriesPayers.getCurrentPayerSettings.useQuery();
  const queryPayoutSettings = useQueryOwnPayoutSettings();

  const fundingSourceId =
    queryPayerSettings.data?.payrollSettings?.defaultFundingSource?.id;

  const isPayrollFundingMethod = useMemo(
    () => fundingSourceId === props.id,
    [props.id, fundingSourceId]
  );

  const isPayoutStandardMethod = useMemo(
    () => queryPayoutSettings.data?.standardDestination?.id === props.id,
    [props.id, queryPayoutSettings.data?.standardDestination?.id]
  );

  const isPayoutInstantMethod = useMemo(
    () => queryPayoutSettings.data?.instantDestination?.id === props.id,
    [props.id, queryPayoutSettings.data?.instantDestination?.id]
  );

  const usage = useMemo(() => {
    const list: string[] = [];

    if (isPayoutInstantMethod) {
      list.push("instant payout method");
    }

    if (isPayoutStandardMethod) {
      list.push("standard payout method");
    }

    if (isPayrollFundingMethod) {
      list.push("payroll funding method");
    }

    return list;
  }, [isPayoutInstantMethod, isPayoutStandardMethod, isPayrollFundingMethod]);

  const [mutate, meta] = useWSMutation<
    void,
    WSServiceError,
    {
      isPayoutStandardMethod?: boolean;
      isPayoutInstantMethod?: boolean;
      isPayrollFundingMethod?: boolean;
    }
  >(
    async () => {
      const payoutSettingsId = queryPayoutSettings.data?.id;

      if (
        (isPayoutStandardMethod || isPayoutInstantMethod) &&
        payoutSettingsId
      ) {
        await payoutsApi.updatePayoutSettings({
          payoutSettingsId,
          payoutSettingsUpdate: {
            ...(isPayoutStandardMethod ? { standardDestination: null } : {}),
            ...(isPayoutInstantMethod ? { instantDestination: null } : {})
          }
        });
      }

      // TODO(v3-blocker) WIN-16076: removing the payroll funding method can no
      // longer clear it. v1 wrote `fundingSource: null`; the v3 update schema
      // has no null form for `payrollSettings.defaultFundingSource`, so a null
      // is rejected 422 ValidationError (its sibling `fundingSourcesByCurrency`
      // and `PayoutSettings.*Destination` are nullable, this one is not). So
      // the setting keeps pointing at the deleted method until the payer picks
      // a new one — the "Account in use" warning above is the only thing
      // telling them to. Restore the clear once the field accepts null.

      switch (props.type) {
        case "Account":
          await externalBankAccountsApi.deleteExternalBankAccount({
            bankAccountId: props.id
          });

          break;
        case "PaymentCard": {
          // The removal is a conditional write and the route rejects `*`, so
          // read the card first: its `version` is the strong ETag, and reading
          // it here (rather than from a rendered query) keeps the precondition
          // current instead of asserting a version the modal opened with.
          const paymentCard = await paymentCardsApi.getPaymentCard({
            paymentCardId: props.id
          });

          await paymentCardsApi.deletePaymentCard({
            paymentCardId: props.id,
            ifMatch: strongETag(paymentCard.version)
          });

          break;
        }
        case "CreditCard": {
          // Legacy Stripe cards live on the v1 client profile's
          // `savedPaymentMethods`, which has no v3 equivalent — and the rows
          // that offer this action are sourced from that same retired read, so
          // no CreditCard can reach here today. Left in place so that if one
          // ever renders again the removal fails loudly (`NotMigrated`) instead
          // of closing the modal as a success.
          const client = await usersService.client.get(userId);

          await usersService.client.update(userId, {
            clientId: userId,
            profile: {
              savedPaymentMethods: (
                client.profile.savedPaymentMethods || []
              ).map(pm => (pm.paymentMethodId === props.id ? null : {}))
            }
          });

          break;
        }
      }
    },
    {
      onSuccess: () => props.onClose(true),
      dependencies: [
        // v1 keys kept for the readers still on v1: the client-payments portal
        // and Bookkeeping (bank accounts), and the client profile that holds
        // the saved credit cards.
        QUERY_USERS_ACCOUNTS,
        QUERY_USERS_CLIENT,
        QUERY_PAYMENT_CARDS,
        queriesPayers.getCurrentPayerSettings.queryKeyBase,
        queriesExternalBankAccounts.listExternalBankAccounts.queryKeyBase,
        queriesExternalBankAccounts.getExternalBankAccount.queryKeyBase,
        queriesPaymentCards.listPaymentCards.queryKeyBase,
        queriesPaymentCards.getPaymentCard.queryKeyBase,
        queriesPayouts.listPayoutSettings.queryKeyBase,
        queriesPayouts.getPayoutSettings.queryKeyBase
      ]
    }
  );

  const { label } = useMethodLabelProps(props.id, props.type);

  return (
    <WSList gap="XL">
      <WSText.ParagraphSm color="gray600">
        Are you sure you want to remove this account?
      </WSText.ParagraphSm>

      <WSCard
        header={{
          label
        }}
      />

      {usage.length > 0 && (
        <WSAlert icon="alert" title="Account in use" theme="warning">
          This bank account is currently used as a {usage.join(", ")}. If you
          remove it, please select another account to set as the default(s).
        </WSAlert>
      )}

      <ErrorAlert context="AddPaymentMethod" error={meta.error} />

      <WSActions
        buttons={[
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: () => props.onClose(false)
          },
          {
            destructive: true,
            label: "Remove",
            onClick: () => {
              mutate({
                isPayoutStandardMethod,
                isPayoutInstantMethod,
                isPayrollFundingMethod
              });
            },
            loading: meta.isLoading
          }
        ]}
      />
    </WSList>
  );
};

export const useModalRemove = () =>
  useWSModal(ModalRemove, {
    title: "Remove account",
    size: "S"
  });
